import { useMountEffect } from "@react-hookz/web";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import type { seriesWidgetContent$key } from "~/queries/__generated__/seriesWidgetContent.graphql";

import { useTracking } from "./hooks";
import { SeriesWidgetEntrypointHeadline } from "./series-widget-entrypoint-headline";
import { SeriesWidgetItem } from "./series-widget-item";
import { Container, StyledEntityLink } from "./styles";

export type Props = {
  className?: string;
  reference: seriesWidgetContent$key;
};

export const SeriesWidget: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const content = useFragment(
    graphql`
      fragment seriesWidgetContent on Article {
        ...seriesWidgetEntrypointHeadlineContent
        entityId
        seriesContainer {
          ...entityLink
          headline
          series {
            ... on Article {
              ...seriesWidgetItemContent
              entityId
            }
          }
        }
      }
    `,
    reference_,
  );

  const { trackClick, trackImpression } = useTracking();

  useMountEffect(() => {
    trackImpression();
  });

  // the default limit
  const seriesLimit = 10;
  const rangeBeforeCurrent = 4;
  const rangeAfterCurrent = 5;

  const isShowMoreOn = useMemo(
    () => (content.seriesContainer?.series?.length ?? 0) > seriesLimit,
    [content.seriesContainer?.series],
  );

  const itemRange = useMemo<[start: number, end: number]>(() => {
    const items = content.seriesContainer?.series ?? [];
    const currentItemIndex = items.findIndex(item => item?.entityId === content.entityId);
    // when current inside the first 'seriesLimit' items
    if (currentItemIndex <= rangeBeforeCurrent) {
      return [0, seriesLimit];
    }
    // when current inside the last 'seriesLimit' items
    if (currentItemIndex > items.length - rangeAfterCurrent - 1) {
      return [items.length - seriesLimit, items.length];
    }
    // in the middle
    return [currentItemIndex - rangeBeforeCurrent, currentItemIndex + rangeAfterCurrent + 1];
  }, [content.entityId, content.seriesContainer?.series]);

  const series = useMemo(() => {
    const items = content.seriesContainer?.series ?? [];
    return isShowMoreOn ? items.slice(...itemRange) : items;
  }, [content.seriesContainer?.series, isShowMoreOn, itemRange]);

  const campaign = useMemo(
    () => `${(content.seriesContainer?.headline ?? "").replaceAll(/\s+/g, "_")}`,
    [content.seriesContainer?.headline],
  );

  return (
    <Container>
      <SeriesWidgetEntrypointHeadline reference={content} />
      {series.map((series, index) =>
        series ? (
          <SeriesWidgetItem
            index={itemRange[0] + index + 1}
            isActive={content.entityId === series.entityId}
            key={series.entityId}
            reference={series}
            onClick={trackClick}
          />
        ) : null,
      )}
      {isShowMoreOn && content.seriesContainer && (
        <StyledEntityLink
          query={{
            campaign,
            module: "series_LHS_more_on",
            pgtype: "article",
          }}
          reference={content.seriesContainer}
        >
          {">"} More on this Series
        </StyledEntityLink>
      )}
    </Container>
  );
};

SeriesWidget.displayName = "SeriesWidget";
